// Content CMS mutation transport shared by the blog and docs sections (A7Y-774).

import type {
  BlogPreviewTokenResponse,
  BlogImageListResponse,
  BlogImageUploadResponse,
  ContentPendingStatusRequest,
  DocsPublishRequest,
  DocsGuardRefusal,
} from '@a7y/types';
import { authFetch, baseUrl, client, rawAdminFetch } from './transport';

// ------------------------------------------------------------------------------------
// Content CMS (A7Y-2068, generalised to both sections at A7Y-4470) mutation
// transport.
//
// Every section's write routes return either a `{ success, data }` envelope
// (create / update / lifecycle) or a bare `{ success }` (delete), and on a Zod
// failure a 400 `{ success, error, details: { fieldErrors, formErrors } }`. A
// docs lifecycle/removal call can also be refused by a publish guard (A7Y-4470
// D5): a 409 `DocsGuardRefusalResponse` — `{ success: false, error, refusals }`.
// The shared `client` throws on non-2xx and swallows that structured body, so
// mutations that need inline field-level validation (or refusal detail) go
// through this helper instead. It unwraps `.data` on success and surfaces
// `fieldErrors` (first message per field) plus any `refusals` on a failure so
// the editor can pin errors to inputs and render exactly what blocked it.
export type ContentMutateResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      status: number;
      error: string;
      fieldErrors?: Record<string, string>;
      refusals?: DocsGuardRefusal[];
    };

/** @deprecated kept as an alias — every call site now goes through {@link ContentMutateResult}. */
export type BlogMutateResult<T> = ContentMutateResult<T>;

function flattenZodFieldErrors(details: unknown): Record<string, string> | undefined {
  const fieldErrors = (details as { fieldErrors?: Record<string, string[]> })?.fieldErrors;
  if (!fieldErrors || typeof fieldErrors !== 'object') return undefined;
  const out: Record<string, string> = {};
  for (const [key, msgs] of Object.entries(fieldErrors)) {
    if (Array.isArray(msgs) && msgs.length > 0) out[key] = msgs[0];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export async function contentMutate<T>(
  path: string,
  method: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<ContentMutateResult<T>> {
  const res = await rawAdminFetch(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const error =
      (json && typeof json.error === 'string' && json.error) ||
      `Request to ${path} failed: ${res.status} ${res.statusText}`;
    return {
      ok: false,
      status: res.status,
      error,
      fieldErrors: flattenZodFieldErrors(json?.details),
      refusals: Array.isArray(json?.refusals) ? (json.refusals as DocsGuardRefusal[]) : undefined,
    };
  }
  // `{ success, data }` envelope → unwrap `data`; otherwise return the raw body
  // (e.g. `DELETE` → `{ success: true }`).
  const data = json && 'data' in json ? (json.data as T) : (json as unknown as T);
  return { ok: true, data };
}

/** @deprecated kept as an alias — every call site now goes through {@link contentMutate}. */
export const blogMutate = contentMutate;

/**
 * The lifecycle transport shared by every content section (A7Y-4470 C5):
 * `api.blog` and `api.docs` are both `{ ...contentSectionApi(basePath),
 * <section-only extras> }`. Reads use the shared `client`; mutations that can
 * surface Zod field errors or guard refusals route through `contentMutate`;
 * image upload uses the raw `authFetch` escape hatch so the browser sets the
 * multipart boundary itself.
 */
export function contentSectionApi<TDoc extends { id: string }, TCreate, TUpdate, TRevision>(
  basePath: string
) {
  return {
    // --- Reads ---
    list: (status?: string) => {
      const sp = new URLSearchParams();
      if (status && status !== 'all') sp.set('status', status);
      const qs = sp.toString();
      return client.get<{ posts: TDoc[]; total: number }>(`${basePath}${qs ? `?${qs}` : ''}`);
    },

    get: (id: string) => client.get<TDoc>(`${basePath}/${id}`),

    revisions: (id: string) =>
      client.get<{ revisions: TRevision[] }>(`${basePath}/${id}/revisions`),

    listImages: (prefix?: string) => {
      const sp = new URLSearchParams();
      if (prefix) sp.set('prefix', prefix);
      const qs = sp.toString();
      return client.get<BlogImageListResponse>(`${basePath}/images${qs ? `?${qs}` : ''}`);
    },

    // --- Content mutations (field-level validation) ---
    create: (data: TCreate) => contentMutate<TDoc>(basePath, 'POST', data),

    update: (id: string, data: TUpdate) => contentMutate<TDoc>(`${basePath}/${id}`, 'PATCH', data),

    remove: (id: string) => contentMutate<{ success: true }>(`${basePath}/${id}`, 'DELETE'),

    // --- Lifecycle transitions ---
    publish: (id: string, body?: DocsPublishRequest) =>
      contentMutate<TDoc>(`${basePath}/${id}/publish`, 'POST', body),

    schedule: (id: string, goLiveAt: string) =>
      contentMutate<TDoc>(`${basePath}/${id}/schedule`, 'POST', { goLiveAt }),

    unpublish: (id: string) => contentMutate<TDoc>(`${basePath}/${id}/unpublish`, 'POST'),

    restoreRevision: (id: string, revisionId: string) =>
      contentMutate<TDoc>(`${basePath}/${id}/revisions/${revisionId}/restore`, 'POST'),

    archive: (id: string) => contentMutate<TDoc>(`${basePath}/${id}/archive`, 'POST'),

    duplicate: (id: string) => contentMutate<TDoc>(`${basePath}/${id}/duplicate`, 'POST'),

    // --- Publishing queue: queue (or clear, with `status: null`) a status change ---
    setPendingStatus: (id: string, data: ContentPendingStatusRequest) =>
      contentMutate<TDoc>(`${basePath}/${id}/pending-status`, 'POST', data),

    // --- Draft preview token (mint on demand; short TTL, never cache) ---
    previewToken: (id: string) =>
      contentMutate<BlogPreviewTokenResponse>(`${basePath}/${id}/preview-token`, 'POST'),

    // --- Image upload (multipart → apg-public R2). 409 → overwrite confirm. ---
    uploadImage: async (
      file: File,
      slug: string,
      opts?: { filename?: string; confirmOverwrite?: boolean }
    ): Promise<
      | { ok: true; data: BlogImageUploadResponse }
      | { ok: false; status: number; error: string; key?: string }
    > => {
      const form = new FormData();
      form.append('file', file);
      form.append('slug', slug);
      if (opts?.filename) form.append('filename', opts.filename);
      if (opts?.confirmOverwrite) form.append('confirmOverwrite', 'true');
      // NOTE: do NOT go through `client`/`rawAdminFetch` — both force a JSON
      // Content-Type, which clobbers the multipart boundary. `authFetch` still
      // injects the Clerk bearer token.
      const res = await authFetch(`${baseUrl}${basePath}/images`, {
        method: 'POST',
        body: form,
      });
      const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;
      if (!res.ok) {
        return {
          ok: false,
          status: res.status,
          error:
            (json && typeof json.error === 'string' && json.error) ||
            `Upload failed: ${res.status} ${res.statusText}`,
          key: json && typeof json.key === 'string' ? (json.key as string) : undefined,
        };
      }
      return { ok: true, data: json as unknown as BlogImageUploadResponse };
    },
  };
}
